<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CmsPagesAPI from 'dashboard/api/cmsPages';
import ProductsAPI from 'dashboard/api/products';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import FileUploader from './FileUploader.vue';

const props = defineProps({
  page: {
    type: Object,
    required: true,
  },
  section: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const products = ref([]);

const formData = ref({
  identifier: '',
  name: '',
  content_type: 'text',
  content_text: '',
  content_json: {},
  content_url: '',
  position: 0,
  visible: true,
  settings: {},
  column_count: 1,
  columns: [],
  restricted: false,
  url_restriction: '',
});

const contentTypes = [
  { value: 'text', label: t('WEBSITE.CMS.SECTION.TYPES.text') },
  { value: 'html', label: t('WEBSITE.CMS.SECTION.TYPES.html') },
  { value: 'markdown', label: t('WEBSITE.CMS.SECTION.TYPES.markdown') },
  { value: 'json', label: t('WEBSITE.CMS.SECTION.TYPES.json') },
  { value: 'image', label: t('WEBSITE.CMS.SECTION.TYPES.image') },
  { value: 'video', label: t('WEBSITE.CMS.SECTION.TYPES.video') },
  { value: 'url', label: t('WEBSITE.CMS.SECTION.TYPES.url') },
  { value: 'button', label: t('WEBSITE.CMS.SECTION.TYPES.button') },
  { value: 'labels', label: t('WEBSITE.CMS.SECTION.TYPES.labels') },
  { value: 'product', label: t('WEBSITE.CMS.SECTION.TYPES.product') },
  { value: 'review', label: t('WEBSITE.CMS.SECTION.TYPES.review') },
  { value: 'card', label: t('WEBSITE.CMS.SECTION.TYPES.card') },
];

const columnCountOptions = [
  { value: 1, label: t('WEBSITE.CMS.SECTION.COLUMNS.ONE') },
  { value: 2, label: t('WEBSITE.CMS.SECTION.COLUMNS.TWO') },
  { value: 3, label: t('WEBSITE.CMS.SECTION.COLUMNS.THREE') },
];

const isEdit = computed(() => !!props.section);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.CMS.SECTION.EDIT_SECTION')
    : t('WEBSITE.CMS.SECTION.NEW_SECTION')
);

const isMultiColumn = computed(() => formData.value.column_count > 1);

const isTextType = computed(() =>
  ['text', 'html', 'markdown'].includes(formData.value.content_type)
);
const isFileType = computed(() =>
  ['image', 'video'].includes(formData.value.content_type)
);
const isUrlType = computed(() => formData.value.content_type === 'url');
const isJsonType = computed(() => formData.value.content_type === 'json');
const isButtonType = computed(() => formData.value.content_type === 'button');
const isLabelsType = computed(() => formData.value.content_type === 'labels');
const isProductType = computed(() => formData.value.content_type === 'product');
const isReviewType = computed(() => formData.value.content_type === 'review');
const isCardType = computed(() => formData.value.content_type === 'card');

// Helper pour vérifier le type de chaque colonne
const isColumnTextType = contentType =>
  ['text', 'html', 'markdown'].includes(contentType);
const isColumnFileType = contentType =>
  ['image', 'video'].includes(contentType);
const isColumnUrlType = contentType => contentType === 'url';
const isColumnJsonType = contentType => contentType === 'json';
const isColumnButtonType = contentType => contentType === 'button';
const isColumnLabelsType = contentType => contentType === 'labels';
const isColumnProductType = contentType => contentType === 'product';
const isColumnReviewType = contentType => contentType === 'review';
const isColumnCardType = contentType => contentType === 'card';

const jsonContent = computed({
  get() {
    return JSON.stringify(formData.value.content_json, null, 2);
  },
  set(value) {
    try {
      formData.value.content_json = JSON.parse(value);
    } catch (e) {
      // Ignorer les erreurs de parsing
    }
  },
});

// Initialiser le formulaire
if (isEdit.value) {
  const section = props.section;
  formData.value = {
    id: section.id,
    identifier: section.identifier || '',
    name: section.name || '',
    content_type: section.content_type || 'text',
    content_text: section.content_text || '',
    content_json: section.content_json ? { ...section.content_json } : {},
    content_url: section.content_url || '',
    position: section.position || 0,
    visible: section.visible !== false,
    settings: section.settings ? { ...section.settings } : {},
    column_count: section.column_count || 1,
    columns: section.columns ? JSON.parse(JSON.stringify(section.columns)) : [],
    restricted: section.restricted || false,
    url_restriction: section.url_restriction || '',
  };
}

// Initialiser content_json pour les nouveaux types si vide
if (
  !formData.value.content_json ||
  typeof formData.value.content_json !== 'object'
) {
  formData.value.content_json = {};
}

// Auto-génération de l'identifier depuis le name
watch(
  () => formData.value.name,
  newName => {
    if (!isEdit.value && newName) {
      formData.value.identifier = newName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }
  }
);

// Initialiser content_json selon le type de contenu
watch(
  () => formData.value.content_type,
  newType => {
    // S'assurer que content_json existe et est un objet
    if (
      !formData.value.content_json ||
      typeof formData.value.content_json !== 'object'
    ) {
      formData.value.content_json = {};
    }

    // Initialiser les champs spécifiques pour le type button
    if (newType === 'button' && !formData.value.content_json.text) {
      formData.value.content_json = {
        text: formData.value.content_json.text || '',
        url: formData.value.content_json.url || '',
        style: formData.value.content_json.style || 'primary',
        target: formData.value.content_json.target || '_self',
      };
    }

    // Initialiser les champs pour le type product
    if (
      newType === 'product' &&
      formData.value.content_json.product_id === undefined
    ) {
      formData.value.content_json = {
        product_id: formData.value.content_json.product_id || null,
      };
    }

    // Initialiser les champs pour le type review (avis)
    if (newType === 'review' && !formData.value.content_json.name) {
      formData.value.content_json = {
        photo: formData.value.content_json.photo || '',
        name: formData.value.content_json.name || '',
        position: formData.value.content_json.position || '',
        rating: formData.value.content_json.rating || 5,
        quote: formData.value.content_json.quote || '',
      };
    }

    // Initialiser les champs pour le type card
    if (newType === 'card' && !formData.value.content_json.title) {
      formData.value.content_json = {
        image: formData.value.content_json.image || '',
        title: formData.value.content_json.title || '',
        description: formData.value.content_json.description || '',
      };
    }
  },
  { immediate: true }
);

// Initialiser les colonnes quand column_count change
watch(
  () => formData.value.column_count,
  (newCount, oldCount) => {
    if (newCount === 1) {
      formData.value.columns = [];
    } else if (newCount !== oldCount) {
      // Initialiser ou ajuster le tableau de colonnes
      const currentColumns = formData.value.columns || [];
      const newColumns = [];

      for (let i = 0; i < newCount; i += 1) {
        if (currentColumns[i]) {
          // S'assurer que content_json existe
          newColumns.push({
            ...currentColumns[i],
            content_json: currentColumns[i].content_json || {},
          });
        } else {
          newColumns.push({
            content_type: 'text',
            content_text: '',
            content_json: {},
            content_url: '',
          });
        }
      }

      formData.value.columns = newColumns;
    }
  }
);

// Helper pour obtenir le contenu JSON d'une colonne
const getColumnJsonContent = column =>
  JSON.stringify(column.content_json || {}, null, 2);

const setColumnJsonContent = (column, value) => {
  try {
    column.content_json = JSON.parse(value);
  } catch (e) {
    // Ignorer les erreurs de parsing
  }
};

// Charger la liste des produits
const fetchProducts = async () => {
  try {
    const response = await ProductsAPI.get();
    products.value = response.data;
  } catch (error) {
    // Ignorer l'erreur silencieusement
  }
};

// Options pour le SelectMenu des produits
const productOptions = computed(() => {
  return products.value.map(product => ({
    id: product.id,
    name: `${product.name} (${product.sku})`,
    value: product.id,
  }));
});

// Charger les produits au montage du composant
onMounted(() => {
  fetchProducts();
});

const handleSubmit = async () => {
  if (!formData.value.identifier || !formData.value.name) {
    useAlert(t('COMPONENTS.FORM.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  // Validation pour la restriction d'URL
  if (formData.value.restricted && !formData.value.url_restriction) {
    useAlert(t('WEBSITE.CMS.SECTION.RESTRICTION.PATTERN_REQUIRED'));
    return;
  }

  loading.value = true;

  try {
    const sectionData = { ...formData.value };

    // Initialiser content_json selon le type pour éviter les erreurs
    if (
      !sectionData.content_json ||
      typeof sectionData.content_json !== 'object'
    ) {
      sectionData.content_json = {};
    }

    // Pour les types button, s'assurer que les champs existent
    if (sectionData.content_type === 'button') {
      sectionData.content_json = {
        text: sectionData.content_json.text || '',
        url: sectionData.content_json.url || '',
        style: sectionData.content_json.style || 'primary',
        target: sectionData.content_json.target || '_self',
      };
    }

    // Pour les types product, s'assurer que product_id existe
    if (sectionData.content_type === 'product') {
      sectionData.content_json = {
        product_id: sectionData.content_json.product_id || null,
      };
    }

    // Pour les types review, s'assurer que tous les champs existent
    if (sectionData.content_type === 'review') {
      sectionData.content_json = {
        photo: sectionData.content_json.photo || '',
        name: sectionData.content_json.name || '',
        position: sectionData.content_json.position || '',
        rating: sectionData.content_json.rating || 5,
        quote: sectionData.content_json.quote || '',
      };
    }

    // Pour les types card, s'assurer que tous les champs existent
    if (sectionData.content_type === 'card') {
      sectionData.content_json = {
        image: sectionData.content_json.image || '',
        title: sectionData.content_json.title || '',
        description: sectionData.content_json.description || '',
      };
    }

    // IMPORTANT: S'assurer que columns est un tableau vide si column_count = 1
    if (sectionData.column_count === 1) {
      sectionData.columns = [];
    } else if (sectionData.columns && Array.isArray(sectionData.columns)) {
      // Pour les colonnes multiples, s'assurer que chaque colonne a content_json initialisé
      sectionData.columns = sectionData.columns.map(col => {
        const cleanCol = {
          content_type: col.content_type || 'text',
          content_text: col.content_text || '',
          content_json:
            col.content_json && typeof col.content_json === 'object'
              ? col.content_json
              : {},
          content_url: col.content_url || '',
        };
        return cleanCol;
      });
    } else {
      // Si columns n'est pas un tableau valide, le forcer à tableau vide
      sectionData.columns = [];
    }

    // S'assurer absolument que columns est un tableau
    if (!Array.isArray(sectionData.columns)) {
      sectionData.columns = [];
    }

    const payload = {
      cms_page: {
        cms_page_sections_attributes: [sectionData],
      },
    };

    // eslint-disable-next-line no-console
    console.log('Payload being sent:', JSON.stringify(payload, null, 2));

    await CmsPagesAPI.update(props.page.id, payload);

    const message = isEdit.value
      ? t('WEBSITE.CMS.API.SUCCESS.SECTION_UPDATED')
      : t('WEBSITE.CMS.API.SUCCESS.SECTION_CREATED');

    useAlert(message);
    emit('save');
  } catch (error) {
    const message = isEdit.value
      ? t('WEBSITE.CMS.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.CMS.API.ERROR.CREATE_FAILED');

    // Afficher les erreurs de validation si disponibles
    if (error.response?.data?.errors) {
      const errors = error.response.data.errors;
      useAlert(`${message}: ${errors.join(', ')}`);
      // eslint-disable-next-line no-console
      console.error('Validation errors:', errors);
    } else {
      useAlert(message);
    }

    // eslint-disable-next-line no-console
    console.error('Full error details:', {
      status: error.response?.status,
      data: error.response?.data,
      payload: error.config?.data,
    });
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer show size="md" @close="handleClose">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ modalTitle }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5 text-n-slate-11" />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <!-- Name -->
          <div class="col-span-2">
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.SECTION.NAME') }}
              <span class="text-n-red-9">*</span>
            </label>
            <Input
              v-model="formData.name"
              :placeholder="t('WEBSITE.CMS.SECTION.NAME_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Identifier -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.SECTION.IDENTIFIER') }}
              <span class="text-n-red-9">*</span>
            </label>
            <Input
              v-model="formData.identifier"
              :placeholder="t('WEBSITE.CMS.SECTION.IDENTIFIER_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <!-- Column Count -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.SECTION.COLUMNS.LABEL') }}
              <span class="text-n-red-9">*</span>
            </label>
            <select
              v-model.number="formData.column_count"
              class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
            >
              <option
                v-for="option in columnCountOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <!-- Info sur les colonnes -->
        <div
          v-if="isMultiColumn"
          class="flex items-start gap-2 p-3 rounded-lg bg-n-blue-2 border border-n-blue-6"
        >
          <span
            class="i-lucide-info size-5 text-n-blue-9 flex-shrink-0 mt-0.5"
          />
          <p class="text-sm text-n-slate-12">
            {{ t('WEBSITE.CMS.SECTION.COLUMNS.INFO') }}
          </p>
        </div>

        <!-- Content pour une seule colonne (mode classique) -->
        <div v-if="!isMultiColumn">
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('WEBSITE.CMS.SECTION.CONTENT_TYPE') }}
            <span class="text-n-red-9">*</span>
          </label>
          <select
            v-model="formData.content_type"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all mb-3"
          >
            <option
              v-for="type in contentTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>

          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('WEBSITE.CMS.SECTION.CONTENT') }}
          </label>

          <!-- Text/HTML/Markdown -->
          <Textarea
            v-if="isTextType"
            v-model="formData.content_text"
            :rows="formData.content_type === 'text' ? 4 : 8"
            placeholder="Saisissez le contenu..."
            class="w-full font-mono text-sm"
          />

          <!-- JSON -->
          <Textarea
            v-else-if="isJsonType"
            v-model="jsonContent"
            :rows="10"
            placeholder='{ "cle": "valeur" }'
            class="w-full font-mono text-sm"
          />

          <!-- File Upload (image/video) -->
          <FileUploader
            v-else-if="isFileType"
            :content-type="formData.content_type"
            :current-url="formData.content_url"
            @update:url="formData.content_url = $event"
          />

          <!-- URL simple -->
          <Input
            v-else-if="isUrlType"
            v-model="formData.content_url"
            placeholder="https://..."
            class="w-full"
          />

          <!-- Button (JSON) -->
          <div v-else-if="isButtonType" class="space-y-3">
            <Input
              v-model="formData.content_json.text"
              :placeholder="t('WEBSITE.CMS.SECTION.BUTTON.TEXT_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.BUTTON.TEXT')"
              class="w-full"
            />
            <Input
              v-model="formData.content_json.url"
              :placeholder="t('WEBSITE.CMS.SECTION.BUTTON.URL_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.BUTTON.URL')"
              class="w-full"
            />
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.SECTION.BUTTON.STYLE') }}
                </label>
                <select
                  v-model="formData.content_json.style"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="primary">
                    {{ t('WEBSITE.CMS.SECTION.BUTTON.STYLE_PRIMARY') }}
                  </option>
                  <option value="secondary">
                    {{ t('WEBSITE.CMS.SECTION.BUTTON.STYLE_SECONDARY') }}
                  </option>
                  <option value="outline">
                    {{ t('WEBSITE.CMS.SECTION.BUTTON.STYLE_OUTLINE') }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.SECTION.BUTTON.TARGET') }}
                </label>
                <select
                  v-model="formData.content_json.target"
                  class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
                >
                  <option value="_self">
                    {{ t('WEBSITE.CMS.SECTION.BUTTON.SAME_TAB') }}
                  </option>
                  <option value="_blank">
                    {{ t('WEBSITE.CMS.SECTION.BUTTON.NEW_TAB') }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <!-- Labels (JSON Array) -->
          <div v-else-if="isLabelsType" class="space-y-3">
            <p class="text-sm text-n-slate-11">
              {{ t('WEBSITE.CMS.SECTION.LABELS.INFO') }}
            </p>
            <Textarea
              :model-value="jsonContent"
              :rows="8"
              placeholder='[{"text": "Label 1", "color": "blue"}, {"text": "Label 2", "color": "green"}]'
              class="w-full font-mono text-sm"
              @update:model-value="jsonContent = $event"
            />
          </div>

          <!-- Product (JSON with product_id) -->
          <div v-else-if="isProductType" class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.CMS.SECTION.PRODUCT.SELECT') }}
              </label>
              <SelectMenu
                v-model="formData.content_json.product_id"
                :options="productOptions"
                :placeholder="
                  t('WEBSITE.CMS.SECTION.PRODUCT.SELECT_PLACEHOLDER')
                "
                class="w-full"
              />
            </div>
            <p class="text-xs text-n-slate-10">
              {{ t('WEBSITE.CMS.SECTION.PRODUCT.INFO') }}
            </p>
          </div>

          <!-- Review/Avis (JSON) -->
          <div v-else-if="isReviewType" class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.CMS.SECTION.REVIEW.PHOTO') }}
              </label>
              <FileUploader
                content-type="image"
                :current-url="formData.content_json.photo"
                @update:url="formData.content_json.photo = $event"
              />
            </div>
            <Input
              v-model="formData.content_json.name"
              :placeholder="t('WEBSITE.CMS.SECTION.REVIEW.NAME_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.REVIEW.NAME')"
              class="w-full"
            />
            <Input
              v-model="formData.content_json.position"
              :placeholder="
                t('WEBSITE.CMS.SECTION.REVIEW.POSITION_PLACEHOLDER')
              "
              :label="t('WEBSITE.CMS.SECTION.REVIEW.POSITION')"
              class="w-full"
            />
            <Input
              v-model.number="formData.content_json.rating"
              type="number"
              min="1"
              max="5"
              :placeholder="t('WEBSITE.CMS.SECTION.REVIEW.RATING_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.REVIEW.RATING')"
              class="w-full"
            />
            <Textarea
              v-model="formData.content_json.quote"
              :rows="4"
              :placeholder="t('WEBSITE.CMS.SECTION.REVIEW.QUOTE_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.REVIEW.QUOTE')"
              class="w-full"
            />
          </div>

          <!-- Card (JSON) -->
          <div v-else-if="isCardType" class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.CMS.SECTION.CARD.IMAGE') }}
              </label>
              <FileUploader
                content-type="image"
                :current-url="formData.content_json.image"
                @update:url="formData.content_json.image = $event"
              />
            </div>
            <Input
              v-model="formData.content_json.title"
              :placeholder="t('WEBSITE.CMS.SECTION.CARD.TITLE_PLACEHOLDER')"
              :label="t('WEBSITE.CMS.SECTION.CARD.TITLE')"
              class="w-full"
            />
            <Textarea
              v-model="formData.content_json.description"
              :rows="4"
              :placeholder="
                t('WEBSITE.CMS.SECTION.CARD.DESCRIPTION_PLACEHOLDER')
              "
              :label="t('WEBSITE.CMS.SECTION.CARD.DESCRIPTION')"
              class="w-full"
            />
          </div>
        </div>

        <!-- Content multi-colonnes -->
        <div v-else-if="isMultiColumn" class="space-y-4">
          <div
            v-for="(column, index) in formData.columns"
            :key="index"
            class="p-4 rounded-lg border border-n-weak bg-n-solid-1"
          >
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              {{
                t('WEBSITE.CMS.SECTION.COLUMNS.COLUMN_N', { number: index + 1 })
              }}
            </h3>

            <!-- Type de contenu de la colonne -->
            <div class="mb-3">
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.CMS.SECTION.CONTENT_TYPE') }}
                <span class="text-n-red-9">*</span>
              </label>
              <select
                v-model="column.content_type"
                class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 focus:border-transparent transition-all"
              >
                <option
                  v-for="type in contentTypes"
                  :key="type.value"
                  :value="type.value"
                >
                  {{ type.label }}
                </option>
              </select>
            </div>

            <!-- Contenu de la colonne -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                {{ t('WEBSITE.CMS.SECTION.CONTENT') }}
              </label>

              <!-- Text/HTML/Markdown -->
              <Textarea
                v-if="isColumnTextType(column.content_type)"
                v-model="column.content_text"
                :rows="column.content_type === 'text' ? 4 : 6"
                placeholder="Saisissez le contenu..."
                class="w-full font-mono text-sm"
              />

              <!-- JSON -->
              <Textarea
                v-else-if="isColumnJsonType(column.content_type)"
                :model-value="getColumnJsonContent(column)"
                :rows="8"
                placeholder='{ "cle": "valeur" }'
                class="w-full font-mono text-sm"
                @update:model-value="setColumnJsonContent(column, $event)"
              />

              <!-- File Upload (image/video) -->
              <FileUploader
                v-else-if="isColumnFileType(column.content_type)"
                :content-type="column.content_type"
                :current-url="column.content_url"
                @update:url="column.content_url = $event"
              />

              <!-- URL simple -->
              <Input
                v-else-if="isColumnUrlType(column.content_type)"
                v-model="column.content_url"
                placeholder="https://..."
                class="w-full"
              />

              <!-- Button (JSON) -->
              <div
                v-else-if="isColumnButtonType(column.content_type)"
                class="space-y-2"
              >
                <Input
                  v-model="column.content_json.text"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.BUTTON.TEXT_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.BUTTON.TEXT')"
                  class="w-full"
                />
                <Input
                  v-model="column.content_json.url"
                  :placeholder="t('WEBSITE.CMS.SECTION.BUTTON.URL_PLACEHOLDER')"
                  :label="t('WEBSITE.CMS.SECTION.BUTTON.URL')"
                  class="w-full"
                />
              </div>

              <!-- Labels (JSON) -->
              <Textarea
                v-else-if="isColumnLabelsType(column.content_type)"
                :model-value="getColumnJsonContent(column)"
                :rows="6"
                placeholder='[{"text": "Label", "color": "blue"}]'
                class="w-full font-mono text-sm"
                @update:model-value="setColumnJsonContent(column, $event)"
              />

              <!-- Product (JSON) -->
              <div v-else-if="isColumnProductType(column.content_type)">
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.SECTION.PRODUCT.SELECT') }}
                </label>
                <SelectMenu
                  v-model="column.content_json.product_id"
                  :options="productOptions"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.PRODUCT.SELECT_PLACEHOLDER')
                  "
                  class="w-full"
                />
              </div>

              <!-- Review/Avis (JSON) -->
              <div
                v-else-if="isColumnReviewType(column.content_type)"
                class="space-y-2"
              >
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.CMS.SECTION.REVIEW.PHOTO') }}
                  </label>
                  <FileUploader
                    content-type="image"
                    :current-url="column.content_json.photo"
                    @update:url="column.content_json.photo = $event"
                  />
                </div>
                <Input
                  v-model="column.content_json.name"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.REVIEW.NAME_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.REVIEW.NAME')"
                  class="w-full"
                />
                <Input
                  v-model="column.content_json.position"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.REVIEW.POSITION_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.REVIEW.POSITION')"
                  class="w-full"
                />
                <Input
                  v-model.number="column.content_json.rating"
                  type="number"
                  min="1"
                  max="5"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.REVIEW.RATING_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.REVIEW.RATING')"
                  class="w-full"
                />
                <Textarea
                  v-model="column.content_json.quote"
                  :rows="3"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.REVIEW.QUOTE_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.REVIEW.QUOTE')"
                  class="w-full"
                />
              </div>

              <!-- Card (JSON) -->
              <div
                v-else-if="isColumnCardType(column.content_type)"
                class="space-y-2"
              >
                <div>
                  <label
                    class="block text-sm font-medium text-n-slate-12 mb-1.5"
                  >
                    {{ t('WEBSITE.CMS.SECTION.CARD.IMAGE') }}
                  </label>
                  <FileUploader
                    content-type="image"
                    :current-url="column.content_json.image"
                    @update:url="column.content_json.image = $event"
                  />
                </div>
                <Input
                  v-model="column.content_json.title"
                  :placeholder="t('WEBSITE.CMS.SECTION.CARD.TITLE_PLACEHOLDER')"
                  :label="t('WEBSITE.CMS.SECTION.CARD.TITLE')"
                  class="w-full"
                />
                <Textarea
                  v-model="column.content_json.description"
                  :rows="3"
                  :placeholder="
                    t('WEBSITE.CMS.SECTION.CARD.DESCRIPTION_PLACEHOLDER')
                  "
                  :label="t('WEBSITE.CMS.SECTION.CARD.DESCRIPTION')"
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- URL Restriction -->
        <div class="space-y-3 p-4 rounded-lg border border-n-weak bg-n-solid-1">
          <div class="flex items-center gap-2">
            <Checkbox v-model="formData.restricted" />
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('WEBSITE.CMS.SECTION.RESTRICTION.ENABLE') }}
            </label>
          </div>

          <div v-if="formData.restricted" class="space-y-2">
            <label class="block text-sm font-medium text-n-slate-12">
              {{ t('WEBSITE.CMS.SECTION.RESTRICTION.PATTERN') }}
            </label>
            <Input
              v-model="formData.url_restriction"
              :placeholder="
                t('WEBSITE.CMS.SECTION.RESTRICTION.PATTERN_PLACEHOLDER')
              "
              class="w-full"
            />
            <p class="text-xs text-n-slate-11">
              {{ t('WEBSITE.CMS.SECTION.RESTRICTION.HELP') }}
            </p>
            <div class="text-xs space-y-1 text-n-slate-11">
              <p class="font-medium mb-1">
                {{ t('WEBSITE.CMS.SECTION.RESTRICTION.EXAMPLE_TITLE') }}
              </p>
              <ul class="list-disc list-inside pl-2 space-y-0.5 font-mono">
                <li>{{ t('WEBSITE.CMS.SECTION.RESTRICTION.EX_WILDCARD') }}</li>
                <li>{{ t('WEBSITE.CMS.SECTION.RESTRICTION.EX_QUERY') }}</li>
                <li>{{ t('WEBSITE.CMS.SECTION.RESTRICTION.EX_REGEX') }}</li>
                <li>{{ t('WEBSITE.CMS.SECTION.RESTRICTION.EX_EXACT') }}</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Visible -->
        <div>
          <label class="flex items-center gap-2">
            <Checkbox v-model="formData.visible" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('WEBSITE.CMS.SECTION.VISIBLE') }}
            </span>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak"
      >
        <Button
          :label="t('WEBSITE.CMS.SECTION.CANCEL')"
          color="slate"
          variant="outline"
          @click="handleClose"
        />
        <Button
          :label="t('WEBSITE.CMS.SECTION.SAVE')"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Drawer>
</template>
